import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
    faCheckCircle,
    faShoppingBag,
    faHome,
    faMapMarkerAlt,
    faPhone,
    faMoneyBillWave,
    faCalendarDays,
    faUser,
    faArrowLeft
} from "@fortawesome/free-solid-svg-icons";

const HerKartOrderSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { order, demo } = location.state || {};

  if (!order) {
    navigate("/herkart/products", { replace: true });
    return null;
  }

  const {
    customer,
    items,
    totalItems,
    subtotal,
    delivery,
    grandTotal,
    paymentMode,
    orderedAt,
    orderNo,
  } = order;

  const orderDate = new Date(orderedAt).toLocaleString();

  return (
    <div className="min-h-screen bg-[#050816] text-white">
      <div className="sticky top-0 z-40 border-b border-white/10 bg-[#050816]/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-4">
          <Link
            to="/herkart/products"
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 hover:bg-white/20"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </Link>

          <div>
            <h1 className="text-xl font-bold">Order Confirmed</h1>

            <p className="text-xs text-slate-200">FoundHer Marketplace</p>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 py-8">
        {demo && (
          <div className="mb-6 rounded-2xl border border-amber-500/20 bg-amber-500/10 p-4 text-sm text-amber-200">
            This is a demo order (sample cart) and was not saved to the server.
          </div>
        )}

        <div className="rounded-3xl border border-emerald-500/20 bg-emerald-500/10 p-8 text-center">
          <FontAwesomeIcon icon={faCheckCircle} className="mx-auto text-7xl text-emerald-400" />

          <h2 className="mt-6 text-3xl text-cyan-200 font-bold">
            Your Order Has Been Placed!
          </h2>

          <p className="mx-auto mt-3 max-w-2xl text-slate-100">
            Thank you for supporting women entrepreneurs through FoundHer. Your
            order has been received successfully and will be processed shortly.
          </p>

          {orderNo && (
            <p className="mt-4 text-sm text-slate-300">
              Order No: <span className="font-semibold text-pink-300">{orderNo}</span>
            </p>
          )}
        </div>

        <div className="mt-8 grid gap-8 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            <div className="rounded-3xl border border-white/10 bg-white/5 p-6">
              <h3 className="mb-5 text-xl text-cyan-100 font-bold">Delivery Details</h3>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="flex items-start gap-3">
                  <FontAwesomeIcon icon={faUser} className="mt-1 text-pink-400" />

                  <div>
                    <p className="text-xs text-slate-200">Customer</p>

                    <p className="text-gray-100 font-semibold">{customer.name}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <FontAwesomeIcon icon={faPhone} className="mt-1 text-pink-400" />

                  <div>
                    <p className="text-xs text-slate-200">Mobile</p>

                    <p className="font-semibold text-slate-100">{customer.mobile}</p>
                  </div>
                </div>

                <div className="md:col-span-2 flex items-start gap-3">
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="mt-1 text-pink-400" />

                  <div>
                    <p className="text-xs text-slate-200">Delivery Address</p>

                    <p className="text-gray-100 font-semibold leading-7">
                      {customer.house}, {customer.street}
                      {customer.landmark ? `, ${customer.landmark}` : ""}
                      <br />
                      {customer.city}, {customer.state} - {customer.pincode}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-white/10 bg-white/5 p-6">
              <h3 className="mb-5 text-xl text-cyan-100 font-bold">Ordered Products</h3>

              <div className="space-y-4">
                {items.map((item, index) => (
                  <div
                    key={item._id || item.productId || index}
                    className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/5 p-4"
                  >
                    <img
                      src={item.image}
                      alt={item.name}
                      className="h-20 w-20 rounded-xl object-cover"
                    />

                    <div className="flex-1">
                      <h4 className="font-semibold text-cyan-50 ">{item.name}</h4>

                      <p className="mt-1 text-sm text-slate-200">
                        Qty: {item.quantity}
                      </p>
                    </div>

                    <div className="text-right">
                      <p className="font-bold text-pink-400">
                        ₹{item.price * item.quantity}
                      </p>

                      <p className="text-xs text-slate-200">₹{item.price} each</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div>
            <div className="sticky top-24 rounded-3xl border border-white/10 bg-white/5 p-6">
              <h3 className="text-xl text-cyan-100 font-bold">Order Summary</h3>

              <div className="mt-6 space-y-4">
                <div className="flex justify-between">
                  <span className="text-slate-200">Items</span>
                  <span>{totalItems}</span>
                </div>

                <div className="flex justify-between">
                  <span className="text-slate-200">Subtotal</span>
                  <span>₹{subtotal}</span>
                </div>

                <div className="flex justify-between">
                  <span className="text-slate-200">Delivery</span>
                  <span>₹{delivery}</span>
                </div>

                <div className="border-t border-white/10 pt-4">
                  <div className="flex justify-between text-xl font-bold">
                    <span>Total</span>

                    <span className="text-pink-400">₹{grandTotal}</span>
                  </div>
                </div>
              </div>

              <div className="mt-6 rounded-2xl border border-emerald-500/20 bg-emerald-500/10 p-4">
                <div className="flex items-start gap-3">
                  <FontAwesomeIcon icon={faMoneyBillWave} className="mt-1 text-emerald-400" />

                  <div>
                    <h4 className="font-semibold text-cyan-50 ">Payment Mode</h4>

                    <p className="text-sm text-slate-100">{paymentMode}</p>
                  </div>
                </div>
              </div>

              <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-4">
                <div className="flex items-start gap-3">
                  <FontAwesomeIcon icon={faCalendarDays} className="mt-1 text-pink-400" />

                  <div>
                    <h4 className="font-semibold text-cyan-50 ">Order Date</h4>

                    <p className="text-sm text-slate-100">{orderDate}</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 space-y-3">
                {orderNo && (
                  <Link
                    to={`/herkart/bill/${orderNo}`}
                    className="flex w-full items-center justify-center gap-3 rounded-2xl border border-pink-500/40 bg-pink-500/10 py-4 font-semibold text-pink-300 transition hover:bg-pink-500/20"
                  >
                    View Bill
                  </Link>
                )}

                <Link
                  to="/herkart/products"
                  className="flex w-full items-center justify-center gap-3 rounded-2xl bg-pink-600 py-4 font-semibold transition hover:bg-pink-500"
                >
                  <FontAwesomeIcon icon={faShoppingBag} />
                  Continue Shopping
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HerKartOrderSuccess;